import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApiFetch } from "../../api/client";
import { useConfirmDialog } from "../../components/ConfirmDialog";
import { Card } from "../../components/ui/Card";
import { Chip } from "../../components/ui/Chip";
import { CallDetailDialog } from "./CallDetailDialog";
import {
  changeInCentsPerLb,
  draftFromSavedCall,
  emptyDraft,
  findDraftProblem,
  FMP_MAX_USD_PER_LB,
  FMP_MIN_USD_PER_LB,
  formatSignedCentsPerLb,
  formatUsdPerLb,
  isOutsideNominationRange,
  periodLabel,
  RATIONALE_MAX_CHARS,
  REVISIT_TRIGGER_MAX_CHARS,
  shiftMonth,
  toCreateRequest,
  type CallDraft,
  type PeriodDraft,
} from "./callForm";
import { CodeGroupedHistory } from "./CodeGroupedHistory";
import { applyCodePick, CODE_FIELD_KEYS } from "./codePicks";
import { errorMessage, monthName, recordedLabel } from "./format";
import { InfoToggle } from "./InfoToggle";
import { CodeField } from "./ResinCodePicker";
import { SectionHeader } from "./SectionHeader";
import type { Call, CallDetail, CallGuidance, LandedPriceUse, RefCode, ResinContext } from "./types";

interface Props {
  month: string;
  resin: ResinContext;
  canWrite: boolean;
  isAdmin: boolean;
  appUserId: number | null;
  logIntelligenceVersion: number;
  onOpenMonth: (month: string) => void;
  onCallsChanged?: () => void;
}

interface CallPreview {
  draft: CallDraft;
  changeFromPriorCallCents: number | null;
  isOutsideNominationRange: boolean;
}

interface CallsForResinAndMonth {
  draftKey: string;
  calls: Call[];
}

type EditablePeriodField = keyof Omit<PeriodDraft, "month">;

export function MakeTheCall({
  month,
  resin,
  canWrite,
  isAdmin,
  appUserId,
  logIntelligenceVersion,
  onOpenMonth,
  onCallsChanged,
}: Props) {
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const { confirm: confirmVoid, dialog: voidConfirmDialog } = useConfirmDialog();

  const { resinFamilyCode } = resin;
  const draftKey = `${resinFamilyCode}|${month}`;
  const unsavedDraftsByResinAndMonth = useRef(new Map<string, CallDraft>());
  const [draft, setDraft] = useState<CallDraft>(() => emptyDraft(month));
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [landedPriceUseOptions, setLandedPriceUseOptions] = useState<RefCode[]>([]);
  const [loadedCalls, setLoadedCalls] = useState<CallsForResinAndMonth | null>(null);
  const [callsLoadError, setCallsLoadError] = useState<string | null>(null);
  const [guidance, setGuidance] = useState<CallGuidance | null>(null);
  const [guidanceLoadError, setGuidanceLoadError] = useState<string | null>(null);
  const [savedCallCount, setSavedCallCount] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [preview, setPreview] = useState<CallPreview | null>(null);
  const [openedCall, setOpenedCall] = useState<CallDetail | null>(null);

  const callsThisMonth = useMemo(
    () => (loadedCalls?.draftKey === draftKey ? loadedCalls.calls : []),
    [loadedCalls, draftKey],
  );
  const myLatestCall = useMemo(
    () => callsThisMonth.find((call) => call.enteredByAppUserId === appUserId),
    [callsThisMonth, appUserId],
  );

  useEffect(() => {
    apiFetchRef
      .current("reference/MakeTheCallLandedPriceUse")
      .then((response) => response.json())
      .then(setLandedPriceUseOptions)
      .catch((error) => setStatusMessage(errorMessage(error)));
  }, []);

  useEffect(() => {
    let isStale = false;
    apiFetchRef
      .current(
        `consultant-cockpit/make-the-call?month=${month}-01&resinFamily=${encodeURIComponent(resinFamilyCode)}`,
      )
      .then((response) => response.json())
      .then((calls: Call[]) => {
        if (isStale) return;
        setLoadedCalls({ draftKey, calls });
        setCallsLoadError(null);
      })
      .catch((error) => {
        if (!isStale) setCallsLoadError(errorMessage(error));
      });
    return () => {
      isStale = true;
    };
  }, [month, resinFamilyCode, draftKey, savedCallCount]);

  useEffect(() => {
    let isStale = false;
    apiFetchRef
      .current(
        `consultant-cockpit/make-the-call/guidance?month=${month}-01&resinFamily=${encodeURIComponent(resinFamilyCode)}`,
      )
      .then((response) => response.json())
      .then((loadedGuidance: CallGuidance) => {
        if (isStale) return;
        setGuidance(loadedGuidance);
        setGuidanceLoadError(null);
      })
      .catch((error) => {
        if (!isStale) setGuidanceLoadError(errorMessage(error));
      });
    return () => {
      isStale = true;
    };
  }, [month, resinFamilyCode, logIntelligenceVersion, savedCallCount]);

  useEffect(() => {
    const unsavedDraft = unsavedDraftsByResinAndMonth.current.get(draftKey);
    setDraft(unsavedDraft ?? emptyDraft(month));
    setHasUnsavedChanges(Boolean(unsavedDraft));
    setPreview(null);
    setStatusMessage(null);
  }, [draftKey, month]);

  useEffect(() => {
    const callsAreForThisResinAndMonth = loadedCalls?.draftKey === draftKey;
    if (!callsAreForThisResinAndMonth || unsavedDraftsByResinAndMonth.current.has(draftKey)) return;
    setDraft(myLatestCall ? draftFromSavedCall(myLatestCall) : emptyDraft(month));
  }, [loadedCalls, draftKey, month, myLatestCall]);

  const updateDraft = (updatedDraft: CallDraft) => {
    unsavedDraftsByResinAndMonth.current.set(draftKey, updatedDraft);
    setDraft(updatedDraft);
    setHasUnsavedChanges(true);
    setPreview(null);
    setStatusMessage(null);
  };

  const updatePeriodField = (periodOffset: number, field: EditablePeriodField, value: string) =>
    updateDraft({
      ...draft,
      periods: draft.periods.map((period, index) =>
        index === periodOffset ? { ...period, [field]: value } : period,
      ),
    });

  const showPreview = (): CallPreview | null => {
    const draftProblem = findDraftProblem(draft);
    if (draftProblem) {
      setStatusMessage(draftProblem);
      setPreview(null);
      return null;
    }
    const priorCall = guidance?.priorCall;
    const changeFromPriorCallCents = priorCall
      ? changeInCentsPerLb(Number(draft.periods[0].fmpUsdPerLb), priorCall.fmpUsdPerLb)
      : null;
    const newPreview: CallPreview = {
      draft,
      changeFromPriorCallCents,
      isOutsideNominationRange:
        changeFromPriorCallCents !== null &&
        isOutsideNominationRange(changeFromPriorCallCents, guidance?.highestNominationCentsPerLb),
    };
    setPreview(newPreview);
    setStatusMessage(null);
    return newPreview;
  };

  const saveCall = async () => {
    if (isSaving || !canWrite || !showPreview()) return;
    setIsSaving(true);
    setStatusMessage("Saving…");
    try {
      const response = await apiFetchRef.current("consultant-cockpit/make-the-call", {
        method: "POST",
        body: JSON.stringify(toCreateRequest(draft, resinFamilyCode)),
      });
      const savedCall: CallDetail = await response.json();
      const wasAlreadyLogged = response.status === 200;
      unsavedDraftsByResinAndMonth.current.delete(draftKey);
      setHasUnsavedChanges(false);
      const evidenceDescription = `${savedCall.evidenceCount} log ${
        savedCall.evidenceCount === 1 ? "record" : "records"
      }`;
      setStatusMessage(
        wasAlreadyLogged
          ? "Already logged: this exact call was saved earlier against the same evidence."
          : `Call and three-month forecast saved · ${recordedLabel(savedCall.recordedUtc)}. ` +
              `${evidenceDescription} kept as evidence.`,
      );
      setSavedCallCount((count) => count + 1);
      onCallsChanged?.();
    } catch (error) {
      setStatusMessage(errorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const openCall = useCallback(async (makeTheCallId: number) => {
    try {
      const response = await apiFetchRef.current(`consultant-cockpit/make-the-call/${makeTheCallId}`);
      setOpenedCall(await response.json());
    } catch (error) {
      setStatusMessage(errorMessage(error));
    }
  }, []);

  const closeOpenedCall = useCallback(() => setOpenedCall(null), []);

  const voidOpenedCall = async () => {
    if (!openedCall) return;
    const voidReason = await confirmVoid({
      title: "Void this call?",
      message: "It stays on record as voided and drops out of this month's history.",
      reasonLabel: "Reason",
      reasonPlaceholder: "e.g. saved against the wrong month",
      confirmLabel: "Void",
    });
    if (voidReason === null) return;
    if (!voidReason.trim()) {
      setStatusMessage("A reason is required to void a call.");
      return;
    }
    try {
      await apiFetchRef.current(`consultant-cockpit/make-the-call/${openedCall.makeTheCallId}/void`, {
        method: "POST",
        body: JSON.stringify({ reason: voidReason.trim() }),
      });
      setOpenedCall(null);
      setStatusMessage("Call voided.");
      setSavedCallCount((count) => count + 1);
      onCallsChanged?.();
    } catch (error) {
      setStatusMessage(errorMessage(error));
    }
  };

  const isReadOnly = !canWrite;
  const followingMonth = shiftMonth(month, 1);
  const canVoidOpenedCall =
    openedCall !== null && canWrite && (isAdmin || openedCall.enteredByAppUserId === appUserId);

  return (
    <section className="lane on" aria-labelledby="call-heading">
      <SectionHeader sectionId="call-section">
        <Chip>{monthName(month)}</Chip>
        <Chip variant="ok">{resinFamilyCode}</Chip>
      </SectionHeader>

      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <aside className="flex flex-col gap-6">
          <Card>
            <h3 className="m-0 mb-3 text-[15px]">Your Judgment</h3>
            {guidanceLoadError ? (
              <p className="m-0 text-rs-light-gray text-[13px]">Couldn't load guidance: {guidanceLoadError}</p>
            ) : !guidance ? (
              <p className="m-0 text-rs-light-gray text-[13px]">Loading…</p>
            ) : (
              <GuidancePanel guidance={guidance} month={month} onOpenCall={openCall} />
            )}
            <InfoToggle label="What do these mean?" className="mt-3">
              <p className="m-0">
                These help you judge your call: the biggest increase suppliers announced this month, how
                many of your tracked suppliers you've checked, your last FMP, and what you predicted for
                this month in an earlier call. Use them as a check, not as the answer.
              </p>
            </InfoToggle>
          </Card>

          <Card>
            <h3 className="m-0 mb-3 text-[15px]">Call &amp; Forecast History</h3>
            {callsLoadError ? (
              <p className="m-0 text-rs-light-gray text-[13px]">Couldn't load calls: {callsLoadError}</p>
            ) : (
              <CallHistoryList calls={callsThisMonth} resin={resin} onOpenCall={openCall} />
            )}
          </Card>
        </aside>

        <form
          className="card"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void saveCall();
          }}
        >
          <div className="mb-5 text-[13px]" role="status">
            {hasUnsavedChanges ? (
              <>
                <b className="text-white">Unsaved call &amp; forecast changes</b>
                <p className="m-0 mt-1 text-rs-light-gray">
                  Save all four months to capture this view. Previously saved submissions are unchanged.
                </p>
              </>
            ) : myLatestCall ? (
              <>
                <b className="text-rs-green">Call saved · {recordedLabel(myLatestCall.recordedUtc)}</b>
                <p className="m-0 mt-1 text-rs-light-gray">
                  Changes below become a new submission. Earlier calls and forecasts stay in history.
                </p>
                <button
                  type="button"
                  className="mt-2 bg-transparent border-none p-0 text-rs-green hover:underline"
                  onClick={() => onOpenMonth(followingMonth)}
                >
                  Open {monthName(followingMonth)} →
                </button>
              </>
            ) : (
              <>
                <b className="text-white">
                  {resinFamilyCode} · {monthName(month)} · open workbook
                </b>
                <p className="m-0 mt-1 text-rs-light-gray">
                  Enter this month's {resinFamilyCode} call and all three forecasts. Saving keeps you in this month.
                </p>
              </>
            )}
          </div>

          <div className="form mb-5">
            {CODE_FIELD_KEYS.map((fieldKey) => (
              <div key={fieldKey} className="f4">
                <CodeField
                  id={`make-the-call-${fieldKey}`}
                  fieldKey={fieldKey}
                  resin={resin}
                  picks={draft}
                  disabled={isReadOnly}
                  onChange={(changedKey, value) => updateDraft(applyCodePick(draft, changedKey, value, resin))}
                />
              </div>
            ))}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {draft.periods.map((period, periodOffset) => (
              <PeriodFields
                key={period.month}
                period={period}
                periodOffset={periodOffset}
                isReadOnly={isReadOnly}
                onChange={(field, value) => updatePeriodField(periodOffset, field, value)}
              />
            ))}
          </div>

          <div className="mt-5">
            <label className="fl" htmlFor="make-the-call-landed-price-use">
              Did the landed-price comparison influence this call?<span className="req">*</span>
            </label>
            <select
              id="make-the-call-landed-price-use"
              className="ctl"
              value={draft.landedPriceUseCode}
              disabled={isReadOnly}
              onChange={(event) =>
                updateDraft({ ...draft, landedPriceUseCode: event.target.value as LandedPriceUse | "" })
              }
            >
              <option value="">Choose an answer</option>
              {landedPriceUseOptions.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.displayName}
                </option>
              ))}
            </select>
            <p className="hint">
              The comparison adjusts import prices for freight, duty and inland delivery. Choosing "No"
              keeps the observation in your workbook.
            </p>
          </div>

          <div className="formfoot">
            <span className="msg">
              {statusMessage ??
                (isReadOnly
                  ? "Viewers can read calls but not make one."
                  : "All four months and their reasoning are required. Save captures today's view and this month's evidence.")}
            </span>
            <button type="button" className="btn ghost" onClick={showPreview} disabled={isReadOnly}>
              <span className="material-symbols-sharp">visibility</span>Preview
            </button>
            <button type="submit" className="btn" disabled={isReadOnly || isSaving}>
              <span className="material-symbols-sharp">save</span>
              {isSaving
                ? "Saving…"
                : myLatestCall
                  ? "Save New Call & Forecast Revision"
                  : "Save My Call & Forecast"}
            </button>
          </div>

          {preview && <PreviewPanel preview={preview} priorCall={guidance?.priorCall} />}
        </form>
      </div>

      {openedCall && (
        <CallDetailDialog
          call={openedCall}
          landedPriceUseOptions={landedPriceUseOptions}
          canVoid={canVoidOpenedCall}
          onVoid={voidOpenedCall}
          onClose={closeOpenedCall}
        />
      )}
      {voidConfirmDialog}
    </section>
  );
}

interface PeriodFieldsProps {
  period: PeriodDraft;
  periodOffset: number;
  isReadOnly: boolean;
  onChange: (field: EditablePeriodField, value: string) => void;
}

function PeriodFields({ period, periodOffset, isReadOnly, onChange }: PeriodFieldsProps) {
  const isAssessment = periodOffset === 0;
  return (
    <fieldset
      className={`border rounded-md p-4 m-0 flex flex-col gap-3 ${
        isAssessment ? "border-rs-green/50" : "border-rs-jet"
      }`}
    >
      <legend className="px-1.5 text-[12.5px] text-rs-off-white">{periodLabel(periodOffset)}</legend>
      <label className="fl m-0">
        {isAssessment ? "Assessment month" : "Forecast month"}
        <input type="month" className="ctl mt-1.5" value={period.month} readOnly />
      </label>
      <label className="fl m-0">
        My FMP · $/lb<span className="req">*</span>
        <input
          type="number"
          className="ctl mt-1.5"
          step="any"
          min={FMP_MIN_USD_PER_LB}
          max={FMP_MAX_USD_PER_LB}
          placeholder="1.22"
          value={period.fmpUsdPerLb}
          disabled={isReadOnly}
          onChange={(event) => onChange("fmpUsdPerLb", event.target.value)}
        />
      </label>
      <label className="fl m-0">
        Why this price?<span className="req">*</span>
        <textarea
          className="ctl mt-1.5"
          rows={3}
          maxLength={RATIONALE_MAX_CHARS}
          value={period.rationale}
          disabled={isReadOnly}
          onChange={(event) => onChange("rationale", event.target.value)}
        />
      </label>
      <label className="fl m-0">
        What would change my view?<span className="req">*</span>
        <textarea
          className="ctl mt-1.5 min-h-18"
          rows={2}
          maxLength={REVISIT_TRIGGER_MAX_CHARS}
          value={period.revisitTrigger}
          disabled={isReadOnly}
          onChange={(event) => onChange("revisitTrigger", event.target.value)}
        />
      </label>
    </fieldset>
  );
}

interface CallHistoryListProps {
  calls: Call[];
  resin: ResinContext;
  onOpenCall: (makeTheCallId: number) => void;
}

function CallHistoryList({ calls, resin, onOpenCall }: CallHistoryListProps) {
  if (calls.length === 0) {
    return <p className="m-0 text-rs-light-gray text-[13px]">No call saved for this month.</p>;
  }
  const latestCallIdByConsultant = new Map<number, number>();
  for (const call of calls) {
    if (!latestCallIdByConsultant.has(call.enteredByAppUserId)) {
      latestCallIdByConsultant.set(call.enteredByAppUserId, call.makeTheCallId);
    }
  }
  return (
    <CodeGroupedHistory
      items={calls}
      resin={resin}
      noun="call"
      isCompact
      recordedUtcOf={(call) => call.recordedUtc}
      renderItems={(groupCalls) => (
        <ul className="flex flex-col gap-1.5 list-none p-0 m-0">
          {groupCalls.map((call) => {
            const isLatestForConsultant =
              latestCallIdByConsultant.get(call.enteredByAppUserId) === call.makeTheCallId;
            const assessmentPeriod = call.periods.find((period) => period.periodOffset === 0);
            const forecastDescription =
              call.periods.length === 4 ? "3-month forecast" : "No forecast recorded";
            return (
              <li key={call.makeTheCallId}>
                <button
                  type="button"
                  className="w-full text-left bg-transparent border border-rs-jet rounded-md px-3 py-2 hover:border-rs-green"
                  onClick={() => onOpenCall(call.makeTheCallId)}
                >
                  <span className="block text-rs-off-white text-[13.5px]">
                    <strong>{assessmentPeriod ? formatUsdPerLb(assessmentPeriod.fmpUsdPerLb) : "—"}</strong> ·{" "}
                    {isLatestForConsultant ? "Latest saved call" : "Earlier submission"}
                  </span>
                  <small className="block text-rs-light-gray text-xs mt-0.5">
                    {call.enteredByDisplayName} · {recordedLabel(call.recordedUtc)} · {forecastDescription}
                  </small>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    />
  );
}

interface GuidancePanelProps {
  guidance: CallGuidance;
  month: string;
  onOpenCall: (makeTheCallId: number) => void;
}

function GuidancePanel({ guidance, month, onOpenCall }: GuidancePanelProps) {
  const highestNominationCents = guidance.highestNominationCentsPerLb;
  const { priorCall, priorForecast } = guidance;
  return (
    <div className="flex flex-col gap-2.5 text-[13px] text-rs-off-white">
      <p className="m-0">
        {highestNominationCents == null ? (
          "No nomination range is established for this month."
        ) : highestNominationCents > 0 ? (
          <>
            Highest nomination: <strong>{formatSignedCentsPerLb(highestNominationCents)}</strong>. A
            rollover-to-increase range is a useful reference, with exceptions.
          </>
        ) : highestNominationCents === 0 ? (
          "Latest announced changes are rollovers."
        ) : (
          <>
            The highest logged nomination is a decrease:{" "}
            <strong>{formatSignedCentsPerLb(highestNominationCents)}</strong>.
          </>
        )}
      </p>
      <p className="m-0 text-rs-light-gray text-xs">
        {guidance.checkedSupplierCount} of {guidance.trackedSupplierCount} tracked suppliers checked for{" "}
        {monthName(month)}.
      </p>
      <p className="m-0">
        {priorCall ? (
          <>
            Prior saved FMP: <strong>{formatUsdPerLb(priorCall.fmpUsdPerLb)}</strong> ·{" "}
            {monthName(priorCall.callMonth.slice(0, 7))}
          </>
        ) : (
          "No earlier saved FMP."
        )}
      </p>
      {priorForecast && (
        <p className="m-0">
          Previously forecast for this month: <strong>{formatUsdPerLb(priorForecast.fmpUsdPerLb)}</strong>{" "}
          <button
            type="button"
            className="bg-transparent border-none p-0 text-rs-green hover:underline"
            onClick={() => onOpenCall(priorForecast.makeTheCallId)}
          >
            View {monthName(priorForecast.callMonth.slice(0, 7))} Forecast ↗
          </button>
        </p>
      )}
    </div>
  );
}

interface PreviewPanelProps {
  preview: CallPreview;
  priorCall: CallGuidance["priorCall"];
}

function PreviewPanel({ preview, priorCall }: PreviewPanelProps) {
  return (
    <div className="mt-5 border border-rs-jet rounded-md p-4" role="status">
      <h3 className="m-0 mb-3 text-[15px]">Ready to Save · {new Date().toLocaleDateString()}</h3>
      <div className="grid gap-3 md:grid-cols-2">
        {preview.draft.periods.map((period, periodOffset) => (
          <article key={period.month} className="text-[13px]">
            <Chip variant={periodOffset === 0 ? "ok" : "info"}>
              {periodOffset === 0 ? "Assessment" : "Forecast"} · {monthName(period.month)}
            </Chip>
            <p className="mt-2 mb-1 text-white text-base">{formatUsdPerLb(Number(period.fmpUsdPerLb))}</p>
            <p className="m-0 mb-1 text-rs-off-white whitespace-pre-wrap">
              <b>Why this price:</b> {period.rationale.trim()}
            </p>
            <p className="m-0 text-rs-off-white whitespace-pre-wrap">
              <b>Revisit when:</b> {period.revisitTrigger.trim()}
            </p>
          </article>
        ))}
      </div>
      {preview.changeFromPriorCallCents !== null && priorCall && (
        <p className="mt-3 mb-0 text-[13px] text-rs-off-white">
          This month: {formatSignedCentsPerLb(preview.changeFromPriorCallCents)} versus{" "}
          {monthName(priorCall.callMonth.slice(0, 7))}
        </p>
      )}
      {preview.isOutsideNominationRange && (
        <p className="mt-2 mb-0 text-[13px] text-rs-light-gray">
          Outside the usual nomination reference range. Your explanation remains the deciding factor.
        </p>
      )}
    </div>
  );
}
